import { useEffect, useState } from 'react';
import type { PanelStatus } from '../api/types';
import { useApi } from '../api/context';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/Toasts';
import { Chip, ErrorNotice, Loading, PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { formatDate, formatMonth, skopjeMonth } from '../lib/dates';
import { errorMessageKey } from '../lib/errors';
import { formatPrice } from '../lib/money';
import { useMerchantState } from '../state/merchant';

/** The merchant web panel subscription: 350 ден. a month, billed on the monthly ZINQ invoice. */
export function PlanPage() {
  const { t, locale } = useI18n();
  const api = useApi();
  const notify = useToast();
  const { panel, panelError, refreshPanel, setPanel } = useMerchantState();
  const [confirm, setConfirm] = useState<'activate' | 'cancel' | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);

  useEffect(() => {
    // Reading the plan also lets the API bill a new month (renewal, like ZINQ Plus).
    refreshPanel().catch((e: unknown) => setLoadError(e));
  }, [refreshPanel]);

  if (!panel) {
    if (loadError || panelError) {
      return (
        <>
          <PageHeader title={t('plan.title')} />
          <ErrorNotice
            error={loadError ?? panelError}
            onRetry={() => {
              setLoadError(null);
              refreshPanel().catch((e: unknown) => setLoadError(e));
            }}
          />
        </>
      );
    }
    return <Loading />;
  }

  const price = formatPrice(panel.priceMinor);

  const run = async (action: 'activate' | 'cancel'): Promise<void> => {
    setBusy(true);
    try {
      const result: PanelStatus | undefined =
        action === 'activate' ? await api.activatePanel() : await api.cancelPanel();
      if (result && typeof result.active === 'boolean') setPanel(result);
      else await refreshPanel();
      notify(t(action === 'activate' ? 'plan.activated' : 'plan.cancelled'));
      setConfirm(null);
    } catch (e) {
      notify(t(errorMessageKey(e)), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const billed = [...panel.billedMonths].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));

  return (
    <>
      <PageHeader title={t('plan.title')} />
      <div className="plan-grid">
        <section className="card plan" aria-labelledby="plan-name" data-testid="plan-card">
          <div className="card-header">
            <h2 id="plan-name">{t('plan.name')}</h2>
            <Chip tone={panel.active ? 'success' : 'neutral'}>
              <span data-testid="plan-status" data-active={panel.active ? 'true' : 'false'}>
                {t(panel.active ? 'plan.status.active' : 'plan.status.inactive')}
              </span>
            </Chip>
          </div>
          <p className="plan-price">{t('plan.price', { price })}</p>
          <p className="muted">{t('plan.billing')}</p>
          {panel.active && panel.activatedAt ? (
            <p>{t('plan.activeSince', { date: formatDate(panel.activatedAt) })}</p>
          ) : null}
          {!panel.active && panel.cancelledAt ? (
            <p className="muted">{t('plan.cancelledOn', { date: formatDate(panel.cancelledAt) })}</p>
          ) : null}

          <h3>{t('plan.includes')}</h3>
          <ul className="checklist">
            <li>{t('plan.feature.reconciliation')}</li>
            <li>{t('plan.feature.export')}</li>
            <li>{t('plan.feature.history')}</li>
          </ul>

          <div className="plan-actions">
            {panel.active ? (
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirm('cancel')}
                data-testid="plan-cancel"
              >
                {t('plan.cancel')}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setConfirm('activate')}
                data-testid="plan-activate"
              >
                {t('plan.activate')}
              </button>
            )}
          </div>
        </section>

        <div className="plan-side">
          <section className="card" aria-labelledby="plan-free">
            <h2 id="plan-free">{t('plan.freeTitle')}</h2>
            <ul className="checklist checklist--muted">
              <li>{t('plan.free.recent')}</li>
              <li>{t('plan.free.qr')}</li>
              <li>{t('plan.free.invoice')}</li>
            </ul>
          </section>
          <section className="card" aria-labelledby="plan-billed">
            <h2 id="plan-billed">{t('plan.billedMonths')}</h2>
            {billed.length === 0 ? (
              <p className="muted">{t('plan.noBilledMonths')}</p>
            ) : (
              <ul className="month-list" data-testid="plan-billed-months">
                {billed.map((m) => (
                  <li key={m}>
                    <span>{formatMonth(m, locale)}</span>
                    <span className="num">{price}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {confirm === 'activate' ? (
        <ConfirmDialog
          title={t('plan.confirmActivate.title')}
          confirmLabel={t('plan.confirmActivate.ok')}
          cancelLabel={t('app.cancel')}
          onConfirm={() => void run('activate')}
          onCancel={() => setConfirm(null)}
          busy={busy}
        >
          <p>{t('plan.confirmActivate.body', { price, month: formatMonth(skopjeMonth(), locale) })}</p>
        </ConfirmDialog>
      ) : null}
      {confirm === 'cancel' ? (
        <ConfirmDialog
          title={t('plan.confirmCancel.title')}
          confirmLabel={t('plan.confirmCancel.ok')}
          cancelLabel={t('plan.confirmCancel.keep')}
          onConfirm={() => void run('cancel')}
          onCancel={() => setConfirm(null)}
          busy={busy}
          danger
        >
          <p>{t('plan.confirmCancel.body')}</p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
